import React, { forwardRef, useState, useImperativeHandle, useRef, useEffect } from 'react';
import { StyleSheet, TextInput, TextInputProps, TextStyle, View, ViewStyle, Platform, StyleProp } from 'react-native';

import colors from '@packages/core/styles/colors';

import { Label } from './label';
import { Typography } from './typography';
import { useHoverState } from './hooks';
import { Icon } from './icon';

interface TextInputState {
    isHovered: boolean;
    isFocused: boolean;
    isDisabled: boolean;
    hasError: boolean;
}

/** How a field draws its error state; each part falls back to the default look. */
export type TextInputErrorPresentation = {
    /** Border width while the field has an error. */
    borderWidth?: number;
    /** Style of the message under the field, on top of the default one. */
    messageStyle?: StyleProp<TextStyle>;
    /** Whether the error icon shows inside the field. */
    showIcon?: boolean;
};

const DEFAULT_ERROR_PRESENTATION: Required<Pick<TextInputErrorPresentation, 'borderWidth' | 'showIcon'>> = {
    borderWidth: 2,
    showIcon: true,
};

export interface TextInputHelperProps extends TextInputProps {
    controlId?: string;
    errorMessage?: string;
    /** Overrides for the error look; without it the field draws the default error state. */
    errorPresentation?: TextInputErrorPresentation;
    inputContainerStyle?: ViewStyle;
    label?: string;
    labelHint?: string;
    labelStyle?: StyleProp<TextStyle>;
    maxDate?: string;
    minDate?: string;
    optional?: boolean;
    required?: boolean;
    rightIcon?: JSX.Element | ((rightIconProps: { inputState: TextInputState }) => JSX.Element);
    showCharCount?: boolean;
    style?: ViewStyle;
    textInputStyle?: TextStyle;
    type?: string;
    labelWrapperStyle?: StyleProp<ViewStyle>;
}

export const TextInputHelper = forwardRef<TextInput | null, TextInputHelperProps>(
    (
        {
            controlId,
            errorMessage,
            errorPresentation,
            inputContainerStyle,
            label,
            labelHint,
            labelStyle,
            maxDate,
            minDate,
            optional,
            required,
            rightIcon,
            showCharCount,
            style,
            labelWrapperStyle,
            textInputStyle,
            type,
            ...props
        },
        ref
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const { isHovered, hoverEventHandlers } = useHoverState();

        const inputRef = useRef<TextInput>(null);

        //@ts-ignore
        useImperativeHandle(ref, () => inputRef.current, []);

        useEffect(() => {
            if (Platform.OS === 'web' && type) {
                inputRef.current &&
                    inputRef.current.setNativeProps({
                        type,
                        max: maxDate,
                        min: minDate,
                    });
            }
        }, [maxDate, minDate, type]);

        const isDisabled = typeof props.editable === 'boolean' && !props.editable;
        const hasError = !!errorMessage;
        const { borderWidth, messageStyle, showIcon } = { ...DEFAULT_ERROR_PRESENTATION, ...errorPresentation };
        const isErrorIconShown = hasError && showIcon;

        const inputState = {
            isHovered,
            isFocused,
            isDisabled,
            hasError,
        };

        return (
            <View style={style}>
                {!!label && (
                    <Label
                        charCount={showCharCount ? `${(props.value || '').length}/${props.maxLength}` : undefined}
                        controlId={controlId}
                        hint={labelHint}
                        optional={optional}
                        required={required}
                        style={labelStyle}
                        wrapperStyle={labelWrapperStyle}
                    >
                        {label}
                    </Label>
                )}

                <View
                    {...hoverEventHandlers}
                    style={[
                        inputBoxStyles({
                            errorBorderWidth: borderWidth,
                            hasError,
                            isDate: type === 'date',
                            isDisabled,
                            isFocused,
                            isHovered,
                        }),
                        inputContainerStyle,
                    ]}
                >
                    <TextInput
                        nativeID={controlId}
                        placeholderTextColor={colors.textInputPlaceHolder}
                        ref={inputRef}
                        {...props}
                        onFocus={(e) => {
                            setIsFocused(true);
                            props.onFocus && props.onFocus(e);
                        }}
                        onBlur={(e) => {
                            setIsFocused(false);
                            props.onBlur && props.onBlur(e);
                        }}
                        underlineColorAndroid="transparent"
                        style={[styles.textInput, textInputStyle]}
                    />

                    <View style={styles.rightIconsWrapper}>
                        {isErrorIconShown && (
                            <Icon style={rightIcon && styles.errorIconSpacer} name="error" color="redOne" />
                        )}

                        {typeof rightIcon === 'function' ? rightIcon({ inputState }) : rightIcon}
                    </View>
                </View>

                {hasError && (
                    <Typography variant="caption" color="redOne" style={[styles.errorMessage, messageStyle]}>
                        {errorMessage}
                    </Typography>
                )}
            </View>
        );
    }
);

const styles = StyleSheet.create({
    inputContainer: {
        height: Platform.select({ web: 40, default: 48 }),
        paddingHorizontal: 12,
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 3,
        borderWidth: 1,
        borderColor: colors.grayFive,
    },
    focusedInput: {
        borderWidth: 2,
        borderColor: colors.blueOne,
    },
    invalidInput: {
        borderWidth: 2,
        borderColor: colors.redOne,
    },
    textInput: {
        flex: 1,
        fontSize: 17,
        lineHeight: 24,
        maxWidth: '100%',
        color: colors.textDarkPrimary,
        textAlignVertical: 'top',
    },
    hoveredInput: {
        shadowColor: colors.black,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.16,
        shadowRadius: 4,
    },
    disabledInput: {
        borderColor: colors.grayTwo,
        color: colors.textDarkSecondary,
        backgroundColor: colors.grayTwo,
    },
    rightIconsWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    errorIconSpacer: {
        marginRight: 12,
    },
    errorMessage: {
        marginTop: 8,
    },
    dateInput: {
        paddingRight: 0,
    },
});

/** What decides how the field box looks. */
type InputBoxState = {
    /** Border width to draw while the field has an error. */
    errorBorderWidth: number;
    /** Whether the field has an error message. */
    hasError: boolean;
    /** Whether the field takes a date. */
    isDate: boolean;
    /** Whether the field is read only. */
    isDisabled: boolean;
    /** Whether the field has focus. */
    isFocused: boolean;
    /** Whether the pointer is over the field. */
    isHovered: boolean;
};

/**
 * The styles of the field box for the state it is in: the base first, then every state that
 * applies, the last one winning.
 *
 * @param state - {@link InputBoxState}
 * @returns the styles to apply, in order
 */
function inputBoxStyles(state: InputBoxState): ViewStyle[] {
    const { errorBorderWidth, hasError, isDate, isDisabled, isFocused, isHovered } = state;
    const isEditable = !isDisabled;
    const isErrorOutlined = isEditable && hasError;
    const isErrorBorderResized = isErrorOutlined && errorBorderWidth !== DEFAULT_ERROR_PRESENTATION.borderWidth;

    const stateStyles: readonly { applies: boolean; style: ViewStyle }[] = [
        { applies: isDate, style: styles.dateInput },
        { applies: isEditable && isHovered, style: styles.hoveredInput },
        { applies: isEditable && isFocused, style: styles.focusedInput },
        { applies: isErrorOutlined, style: styles.invalidInput },
        { applies: isErrorBorderResized, style: { borderWidth: errorBorderWidth } },
        { applies: isDisabled, style: styles.disabledInput },
    ];

    return [styles.inputContainer, ...stateStyles.filter(({ applies }) => applies).map(({ style }) => style)];
}
